import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Kbd } from '../components/Kbd';
import { useModifierLabel } from '../lib/keyboard';
import { useNavGroups, type NavGroupState } from '../lib/navGroups';
import { useSession, useSignOut } from '../features/auth/queries';
import { AnnouncementBanner } from '../features/mail/AnnouncementBanner';
import { useInboundCount } from '../features/mail/queries';
import { SecurityPage } from '../features/auth/SecurityPage';
import { CommandPalette } from '../features/palette/CommandPalette';
import { openCommandPalette } from '../features/palette/open';
import { VersionLink } from '../features/system/VersionLink';
import {
  activeGroup,
  entriesFor,
  SIDEBAR,
  type NavigationEntry,
  type NavigationSection,
} from './navigation';

/**
 * Das Grundgeruest der Agentenoberflaeche.
 *
 * Helle Kopfleiste, Navigation links, Inhalt rechts -- das Raster aus
 * docs/02, Abschnitt 5.
 *
 * Der Sprunglink ganz oben ist kein Beiwerk: Ohne ihn muss sich jemand, der
 * mit der Tastatur arbeitet, auf jeder Seite erneut durch die vollstaendige
 * Navigation bewegen, bevor er den Inhalt erreicht.
 *
 * **Die Eintraege stehen nicht hier, sondern in ``app/navigation.ts``.** Die
 * Befehlspalette braucht dieselbe Liste; zwei Listen waeren nach dem
 * naechsten neuen Bereich verschieden, und die Palette fuehrte an eine
 * Stelle, die es in der Navigation nicht mehr gibt. Auch die Gliederung steht
 * dort: Diese Datei zeichnet, was die Liste sagt, und weiss von keinem
 * einzigen Bereich.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const session = useSession();
  const signOut = useSignOut();
  const modifier = useModifierLabel();

  // Die Eintraege kommen aus ``app/navigation.ts`` -- derselben Liste, aus
  // der sich die Befehlspalette bedient. Zwei Listen waeren nach dem
  // naechsten neuen Bereich verschieden.
  const permissions = session.data?.permissions ?? [];

  // Welche Gruppe die aufgerufene Seite traegt. Sie klappt auf, sobald man in
  // ihr steht: Wer die Kategorien oeffnet, soll in der Leiste sehen, wo er
  // ist, und nicht eine zugeklappte Gruppe namens "Klassifizierung".
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const groups = useNavGroups();
  const { reveal } = groups;
  const current = activeGroup(pathname);

  useEffect(() => {
    if (current !== undefined) reveal(current);
  }, [current, reveal]);

  // **Und der aufgeklappte Eintrag muss auch zu sehen sein.** Die Leiste rollt
  // seit den Gruppen fuer sich; stehen mehrere Faecher offen, liegt der
  // aktive Eintrag womoeglich unterhalb des sichtbaren Bereichs. Der Balken
  // am Rand ist das einzige "Sie sind hier", das die Leiste hat -- unsichtbar
  // ist er keines.
  //
  // F5-16: Wer administrative Rechte traegt und keinen zweiten Faktor
  // eingerichtet hat, kommt an keinen fachlichen Endpunkt heran -- der Server
  // antwortet mit 403. Statt den Anwender in eine Ansicht laufen zu lassen,
  // die sich nur mit Fehlermeldungen fuellt, steht hier die Einrichtung. Das
  // ist Bequemlichkeit, keine Absicherung: Durchgesetzt wird die Pflicht am
  // Server, und zwar bei jeder einzelnen Anfrage.
  const mustSetUpMfa =
    (session.data?.mfa_required ?? false) && !(session.data?.mfa_enabled ?? false);

  const currentTenant = (session.data?.available_tenants ?? []).find(
    (tenant) => tenant.id === session.data?.tenant_id,
  );

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('nav.skipToContent')}
      </a>

      <header className="shell__bar">
        <span className="shell__brand">
          {t('app.name')}
          <span className="shell__tagline">{t('app.tagline')}</span>
        </span>

        <span className="shell__spacer" />

        {/* Das Kuerzel steht sichtbar in der Kopfleiste und nicht nur im
            Handbuch: Eine Abkuerzung, von der niemand weiss, spart niemandem
            etwas. Es ist ein Knopf und kein Hinweis -- wer kein Kuerzel
            tippen mag, klickt darauf. */}
        <button type="button" className="shell__palette" onClick={openCommandPalette}>
          <Icon name="search" />
          <span className="shell__palette-label">{t('palette.open')}</span>
          <Kbd>{modifier}</Kbd>
          <Kbd>K</Kbd>
        </button>

        <div className="shell__user">
          {currentTenant ? <span className="shell__tenant">{currentTenant.name}</span> : null}
          {session.data ? (
            <Link to="/app/security" className="shell__link">
              {session.data.full_name}
            </Link>
          ) : null}
          <Button
            variant="ghost"
            busy={signOut.isPending}
            onClick={() => {
              signOut.mutate();
            }}
            style={{ color: 'inherit' }}
          >
            {t('auth.signOut')}
          </Button>
        </div>
      </header>

      <div className="shell__body">
        <nav className="shell__nav" aria-label={t('app.name')} hidden={mustSetUpMfa}>
          {/* Ein Block erscheint nur, wenn wenigstens ein Eintrag darin
              erlaubt ist. Das Ausblenden ist Bequemlichkeit, keine
              Absicherung -- der Server prueft jeden Aufruf ohnehin (docs/04,
              Abschnitt 5). */}
          {SIDEBAR.map((section) => (
            <NavSection
              key={section.id}
              section={section}
              permissions={permissions}
              groups={groups}
            />
          ))}

          {/* Unter dem Handbuch, mittig: die laufende Fassung. Ein Klick
              fuehrt auf die Statusseite. */}
          <VersionLink permissions={permissions} />
        </nav>

        <main className="shell__main" id="main">
          {/* Stoerungsbanner ganz oben -- F3-10, K-56. Wer gerade an einem
              Ticket schreibt, soll wissen, dass die Telefonanlage steht. */}
          <AnnouncementBanner />
          {mustSetUpMfa ? <SecurityPage /> : <Outlet />}
        </main>
      </div>

      {/* Ausserhalb des Inhalts: Sie gehoert keiner Seite und ueberlebt den
          Seitenwechsel, den sie selbst ausloest. Waehrend der zweite Faktor
          fehlt, gibt es sie nicht -- sie fuehrte an Endpunkte, die der Server
          ohnehin mit 403 beantwortet. */}
      {mustSetUpMfa ? null : <CommandPalette />}
    </div>
  );
}

/**
 * Ein Block der Seitenleiste.
 *
 * Er erscheint gar nicht, wenn kein Eintrag darin erlaubt ist -- eine
 * Ueberschrift ueber nichts waere ein Versprechen, das die Rechte nicht
 * einloesen. Dasselbe gilt fuer jede einzelne Gruppe darin.
 */
function NavSection({
  section,
  permissions,
  groups,
}: {
  section: NavigationSection;
  permissions: readonly string[];
  groups: NavGroupState;
}) {
  const { t } = useTranslation();

  const filled = section.groups
    .map((group) => ({ ...group, entries: entriesFor(group.id, permissions) }))
    .filter((group) => group.entries.length > 0);

  if (filled.length === 0) return null;

  return (
    <div className={section.footer ? 'nav__block nav__block--footer' : 'nav__block'}>
      {section.labelKey ? <p className="nav__section">{t(section.labelKey)}</p> : null}

      {filled.map((group) =>
        section.collapsible === true && group.labelKey !== undefined ? (
          <NavGroup
            key={group.id}
            id={group.id}
            labelKey={group.labelKey}
            entries={group.entries}
            open={groups.isOpen(group.id)}
            onToggle={() => {
              groups.toggle(group.id);
            }}
          />
        ) : (
          <div className="nav" key={group.id}>
            {group.entries.map((entry) => (
              <NavItem key={entry.to} entry={entry} />
            ))}
          </div>
        ),
      )}
    </div>
  );
}

/**
 * Eine aufklappbare Gruppe -- nur die Einstellungen haben welche.
 *
 * Ein Knopf und keine anklickbare Ueberschrift: ``aria-expanded`` sagt einer
 * Sprachausgabe, dass sich hier etwas oeffnen laesst und ob es offen ist, und
 * ``aria-controls`` sagt, was. Eine Ueberschrift mit ``onClick`` waere fuer
 * das Auge dasselbe und fuer die Tastatur nichts.
 *
 * Die Zahl daneben ist kein Schmuck: Zugeklappt sind fuenf Gruppen fuenf
 * gleich aussehende Zeilen, und sie sagt, was dahinter liegt.
 *
 * **Nicht ``components/Accordion``.** Das ist ein Fliesstext-Akkordeon mit
 * eigenen Ueberschriften und eigenem Zustand. Hier haelt den Zustand
 * ``lib/navGroups`` -- er ueberlebt den Seitenwechsel und wird von aussen
 * gesetzt, wenn die aufgerufene Seite in der Gruppe liegt. Aus dem Akkordeon
 * eines zu machen, das beides kann, waere mehr Verflechtung als Ersparnis.
 */
function NavGroup({
  id,
  labelKey,
  entries,
  open,
  onToggle,
}: {
  id: string;
  labelKey: string;
  entries: readonly NavigationEntry[];
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const panelId = `nav-group-${id}`;

  return (
    <div className="nav__group">
      <button
        type="button"
        className="nav__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <Icon name="chevron" />
        <span className="nav__toggle-label">{t(labelKey)}</span>
        <span className="nav__count">{entries.length}</span>
      </button>

      {/* ``hidden`` und nicht ausgelassen: Der Bereich, auf den
          ``aria-controls`` zeigt, muss es geben, sonst zeigt die Angabe ins
          Leere. */}
      <div className="nav" id={panelId} hidden={!open}>
        {entries.map((entry) => (
          <NavItem key={entry.to} entry={entry} />
        ))}
      </div>
    </div>
  );
}

/** Ein Eintrag der Navigation. */
function NavItem({ entry }: { entry: NavigationEntry }) {
  const { t } = useTranslation();

  return (
    <Link
      to={entry.to}
      className="nav__item"
      // Nur die Ticketliste braucht das: ``/app/tickets`` ist sonst auch
      // dann hervorgehoben, wenn ein einzelnes Ticket offen ist -- und dann
      // stimmen zwei Eintraege gleichzeitig.
      activeOptions={entry.to === '/app/tickets' ? { exact: true } : undefined}
    >
      <Icon name={entry.icon} />
      {t(entry.labelKey)}
      {entry.badge === 'inbound' ? <InboundBadge /> : null}
    </Link>
  );
}

/**
 * Wie viele Anfragen warten -- neben dem Eintrag "Anfragen".
 *
 * Gezeichnet wird nur, was groesser als null ist: Eine Null neben jedem
 * Eintrag ist Rauschen, und eine Warteschlange, die man erst oeffnen muss,
 * um zu sehen, dass sie leer ist, wird nicht geoeffnet. Abgefragt wird
 * nur, wo der Eintrag steht -- und er steht nur mit ``mail.triage``.
 */
function InboundBadge() {
  const { t } = useTranslation();
  const waiting = useInboundCount(true).data?.count ?? 0;

  if (waiting === 0) return null;

  return (
    <>
      <span className="count-bubble nav__badge" aria-hidden="true">
        {waiting}
      </span>
      {/* Die Zahl allein sagt einem vorlesenden Programm nichts: "Anfragen 7"
          koennte auch eine Ordnungsnummer sein. */}
      <span className="visually-hidden">{t('inbound.waiting', { count: waiting })}</span>
    </>
  );
}
